import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import type {
  KBArticle,
  KBArticleListItem,
  KBArticleListResponse,
  KBCategory,
  KBStatus,
} from "@/types";

export function useArticles(filters?: {
  category?: KBCategory;
  status?: KBStatus;
  search?: string;
}) {
  const hasSearch = !!filters?.search;

  return useQuery<KBArticleListItem[]>({
    queryKey: ["kb-articles", filters],
    queryFn: async () => {
      if (hasSearch) {
        // Use dual search endpoint (keyword + semantic)
        const params = new URLSearchParams({ q: filters!.search! });
        const res = await apiClient.get<KBArticleListResponse>(`/kb/search?${params.toString()}`);
        // Apply client-side category/status filters on top of search results
        let articles = res.data.articles;
        if (filters?.category) {
          articles = articles.filter((a) => a.category === filters.category);
        }
        if (filters?.status) {
          articles = articles.filter((a) => a.status === filters.status);
        }
        return articles;
      }

      // Standard list with filters
      const params = new URLSearchParams();
      if (filters?.category) params.set("category", filters.category);
      if (filters?.status) params.set("status", filters.status);
      const res = await apiClient.get<KBArticleListResponse>(`/kb?${params.toString()}`);
      return res.data.articles;
    },
  });
}

export function useArticle(id: string | undefined) {
  return useQuery<KBArticle>({
    queryKey: ["kb-article", id],
    queryFn: async () => {
      const res = await apiClient.get<KBArticle>(`/kb/${id}`);
      return res.data;
    },
    enabled: !!id,
  });
}

export function useCreateArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiClient.post<KBArticle>("/kb", data);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["kb-articles"] }),
  });
}

export function useUpdateArticle(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiClient.put<KBArticle>(`/kb/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["kb-articles"] });
      qc.invalidateQueries({ queryKey: ["kb-article", id] });
    },
  });
}

export function useDeleteArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/kb/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["kb-articles"] }),
  });
}
